import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/nextjs';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { createNote, getClientNotes } from 'queries/clients';
import { addLeadNote, getLeadById } from 'queries/leads';

import { useUI } from 'providers/ui';

const Loader = dynamic(() => import('components/atoms/loader'));

type ActivityNoteComposeProps = {
  leadId?: string | null;
  userId?: string | null;
  compact?: boolean;
  showHeading?: boolean;
  onSaved?: () => void;
  onCancel?: () => void;
};

export default function ActivityNoteCompose({
  leadId,
  userId,
  compact = false,
  showHeading = true,
  onSaved,
  onCancel,
}: ActivityNoteComposeProps) {
  const [saving, setSaving] = useState(false);
  const ui = useUI();
  const { register, handleSubmit, reset } = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const [createLeadNote] = useMutation(addLeadNote);
  const [createUserNote] = useMutation(createNote);

  const saveNote = async (data: { content?: string }) => {
    if (!data.content?.trim()) return;

    try {
      setSaving(true);

      if (leadId) {
        await createLeadNote({
          variables: {
            data: {
              lead: leadId,
              noteByNote: {
                data: {
                  content: data.content.trim(),
                  title: '',
                },
              },
            },
          },
          refetchQueries: [
            {
              query: getLeadById,
              variables: { id: leadId },
            },
          ],
        });
      } else if (userId) {
        await createUserNote({
          variables: {
            data: {
              user: userId,
              noteByNote: {
                data: {
                  content: data.content.trim(),
                  title: '',
                },
              },
            },
          },
          refetchQueries: [
            {
              query: getClientNotes,
              variables: { userId },
            },
          ],
        });
      } else {
        return;
      }

      reset();
      ui.setToast(true, {
        type: 'success',
        title: 'New note added',
      });
      onSaved?.();
    } catch (err) {
      console.log(err);
      ui.setToast(true, {
        type: 'error',
        title: 'Error adding note',
      });
      Sentry.captureException(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(saveNote)}>
      {showHeading ? (
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Add note
        </p>
      ) : null}
      <div>
        <label htmlFor="activity-note" className="sr-only">
          Add note
        </label>
        <textarea
          id="activity-note"
          name="content"
          rows={compact ? 2 : 5}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
          placeholder="Add a note"
          {...register('content', { required: true })}
        />
      </div>
      <div
        className={`${compact ? 'mt-2' : 'mt-4'} flex items-center ${
          onCancel ? 'justify-end gap-2' : 'justify-end'
        }`}
      >
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Cancel
          </button>
        ) : null}
        <button
          disabled={saving}
          type="submit"
          className="inline-flex items-center justify-center rounded-md border border-transparent bg-purple-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
        >
          {saving ? (
            <>
              <Loader
                // @ts-ignore
                styles={{}}
                stroke="#fff"
                width="24"
                height="24"
              />
              <span className="ml-2">Saving</span>
            </>
          ) : (
            'Add note'
          )}
        </button>
      </div>
    </form>
  );
}
